// First-touch marketing attribution, captured with no consent footprint. UTM tags
// from the ad link a visitor arrived through (Facebook/Instagram etc.) are read from
// the URL and kept in sessionStorage — tab-scoped, first-party, cleared when the tab
// closes. That is not a cookie and not persistent cross-site tracking: the tags travel
// only as far as the order the visitor chooses to place, where they power the admin
// "sales by source" report. No cookie banner is required for this.

const KEY = "attribution";
const FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export type Attribution = {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
};

// Read UTM tags from the current URL and, if any are present and nothing has been
// captured yet this session, stash them as the first touch. Safe to call repeatedly:
// once set, later navigations to param-less pages never overwrite the original ad
// source, so the report credits the campaign the visitor actually came in through.
export function captureAttribution(): void {
    try {
        if (sessionStorage.getItem(KEY)) return;

        const params = new URLSearchParams(window.location.search);
        const captured: Record<string, string> = {};

        for (const field of FIELDS) {
            const value = params.get(field);
            if (value) captured[field] = value.slice(0, 200);
        }

        if (Object.keys(captured).length === 0) return;

        sessionStorage.setItem(KEY, JSON.stringify(captured));
    } catch {
        // Private mode / storage disabled: attribution is best-effort, so skip silently.
    }
}

// The captured first touch, shaped for the order request body. Empty object when the
// visitor arrived without UTM tags (organic/direct) — spreading it then adds nothing.
export function getAttribution(): Attribution {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return {};

        const stored = JSON.parse(raw) as Record<string, string>;
        return {
            utmSource: stored.utm_source,
            utmMedium: stored.utm_medium,
            utmCampaign: stored.utm_campaign,
            utmContent: stored.utm_content,
            utmTerm: stored.utm_term,
        };
    } catch {
        return {};
    }
}
